import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { RouteAction } from '@design/buttons/action-config';
import { LoggingService } from '@logging/logging.service';
import { BurgerBarConfig } from './burger-bar.config';
import { AppLayoutStateService } from '../app-layout.state-service';
import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';
import { payrollRoutes } from '@app/payrolls/payrolls-routes';
import { messagingRoutes } from '@app/messaging/messaging-routes';
import { userRoutes } from '@security/users/user-routes';
import { organisationRoutes } from '@security/organisations/organisation-routes';
import { accessControlRoutes } from '@app/access-control/access-control-routes';
import { adminAreaRoutes } from '@app/admin-area/admin-area-routes';
import { designSystemRoutes } from '@app/design-system/design-system-routes';
import { employeeHubRoutes } from '@app/employee-hub/employee-hub-routes';
import { ActiveCintraProductService } from '@app/active-cintra-product.service';
import { CintraProduct } from '@app/cintra-product';
import { Member } from '@security/users/models/member';
import { HolidayApproverSummary } from '@app/employee-hub/holidays/holiday-approvals';
import { integrationsRoutes } from '@app/integrations-admin/integrations-routes';
import { NavigationRoutingService } from '@utils/navigation-routing.service';
import { holidayAllowanceRoutes } from '@app/admin-hub/holiday-allowances/holiday-allowance-routes';
import { getBureauNavigationLinks } from '@app/app-layout/header/product-navigation/bureau-navigation/bureau-navigation-links';
import { FeatureFlagService, FeatureFlagsServiceInjectionToken } from '@feature-flags/feature-flag.service';
import { customReportTypesRoutes } from '@app/custom-reports-admin/custom-report-types-routes';

/**
 * Service for managing the models of the {@link BurgerBarComponent}
 * Items are configured by {@link BurgerBarConfig} and set up in {@link CoreModule}
 */
@Injectable({ providedIn: 'root' })
export class BurgerBarService {
  burgerBarItems$ = new BehaviorSubject<RouteAction[]>(undefined);

  constructor(
    private activeSecurity: ActiveSecurityContextStateService,
    activeCintraProductService: ActiveCintraProductService,
    public appLayoutStateService: AppLayoutStateService,
    private router: Router,
    private navigationRoutingService: NavigationRoutingService,
    private logger: LoggingService,
    @Inject(FeatureFlagsServiceInjectionToken) private featureFlagService: FeatureFlagService
  ) {
    combineLatest([activeSecurity.activeMembership$, activeCintraProductService.activeProduct$]).subscribe(
      ([member, product]: [Member, CintraProduct]) => {
        if (product === 'EmployeeHub') {
          this.buildEmployeeHubItems();
        } else if (product === 'AdminHub') {
          this.buildAdminHubItems();
        } else if (product === 'Bureau') {
          this.burgerBarItems$.next(
            getBureauNavigationLinks(this.activeSecurity.activeBureau, this.activeSecurity, this.router, this.featureFlagService)
          );
        } else {
          this.buildPayrollItems(member);
        }
      }
    );

    this.watchForNavigationChanges();
  }

  private buildPayrollItems = (member: Member) => {
    this.burgerBarItems$.next([
      {
        label: 'Payrolls',
        anyOrganisationPermissions: ['AccessPayrolls'],
        route: 'employers',
        feature: 'Payrolls'
      },
      {
        label: 'Reports',
        anyOrganisationPermissions: ['AccessPayrollReports'],
        route: payrollRoutes.reports,
        feature: 'Reports'
      },
      {
        label: 'Messaging',
        anyOrganisationPermissions: ['AccessMessaging'],
        route: messagingRoutes.inbox,
        enabledAtOrganisation: member.messagingEnabled,
        feature: 'Messaging'
      },
      {
        label: 'Groups',
        anyOrganisationPermissions: ['AccessGroups'],
        route: accessControlRoutes.manageGroupsHub,
        enabledAtOrganisation: member.groupsEnabled,
        feature: 'Groups'
      },
      {
        label: 'Manage users',
        anyOrganisationPermissions: ['EditUsers'],
        route: userRoutes.hub,
        section: 'ManageUsers'
      },
      {
        label: 'Manage Cintra users',
        anyApplicationPermissions: ['EditCintraEmployees'],
        route: userRoutes.cintraHub,
        section: 'ManageCintraUsers'
      },
      {
        label: 'Manage organisations',
        anyApplicationPermissions: ['EditOrganisations'],
        route: organisationRoutes.hub,
        section: 'ManageOrganisations'
      },
      {
        label: 'Manage integrations',
        anyOrganisationPermissions: ['ManageIntegrations'],
        route: integrationsRoutes.hub,
        section: 'ManageIntegrations'
      },
      {
        label: 'Manage Custom Reports',
        anyApplicationPermissions: ['CreateCustomReports'],
        route: customReportTypesRoutes.hub,
        section: 'ManageCustomReports'
      },
      {
        label: 'Cintra Admin Area',
        anyApplicationPermissions: ['EditOrganisations'],
        route: adminAreaRoutes.hub,
        section: 'CintraAdminArea'
      },
      {
        label: 'Design system',
        anyApplicationPermissions: ['EditOrganisations'],
        featureFlag: 'designSystemFeature',
        featureFlagBooleanValue: 'on',
        route: designSystemRoutes.home,
        section: 'DesignSystem'
      }
    ]);
  };

  private buildEmployeeHubItems = () => {
    const holidayApproverSummary$ =
      this.activeSecurity.canAccessEmployeeHub() && this.activeSecurity.hasFeaturePackAuthorityTo('AccessEmployeeHubHolidays')
        ? this.appLayoutStateService.holidayApproverSummary$
        : of({
            isHolidayApprover: false
          } as HolidayApproverSummary);

    holidayApproverSummary$.subscribe((holidayApproverSummary) => {
      const burgerBarItems: RouteAction[] = [
        {
          label: 'Dashboard',
          route: employeeHubRoutes.dashboard,
          anyEmploymentFeaturePackPermissions: ['AccessEmployeeHub'],
          feature: 'EmployeeHubDashboard'
        },
        {
          label: 'Holidays',
          route: employeeHubRoutes.holidays,
          anyEmploymentFeaturePackPermissions: ['AccessEmployeeHubHolidays'],
          feature: 'Holidays'
        },
        {
          label: 'Payslips',
          route: employeeHubRoutes.payslips,
          anyEmploymentFeaturePackPermissions: ['AccessEmployeeHubDocuments'],
          feature: 'Payslips'
        },
        {
          label: 'Documents',
          route: employeeHubRoutes.documents,
          anyEmploymentFeaturePackPermissions: ['AccessEmployeeHubDocuments'],
          feature: 'HubDocuments'
        },
        {
          label: 'Profile',
          route: employeeHubRoutes.profile,
          anyEmploymentFeaturePackPermissions: ['AccessEmployeeHubPersonalDetails'],
          feature: 'MyProfile'
        }
      ];

      if (holidayApproverSummary.isHolidayApprover) {
        burgerBarItems.push({
          label: 'Approvals',
          route: employeeHubRoutes.holidayApprovalsRequests,
          anyEmploymentFeaturePackPermissions: ['AccessEmployeeHubHolidays'],
          feature: 'HolidayApprovals'
        });

        burgerBarItems.push({
          label: 'Approvals History',
          route: employeeHubRoutes.holidayApprovalsHistory,
          anyEmploymentFeaturePackPermissions: ['AccessEmployeeHubHolidays'],
          feature: 'HolidayApprovals'
        });
      }

      this.burgerBarItems$.next(burgerBarItems);
    });
  };

  private buildAdminHubItems = () => {
    this.burgerBarItems$.next([
      {
        label: 'Manage Holidays',
        route: holidayAllowanceRoutes.manageHolidayAllowances,
        anyFeaturePackPermissions: ['AccessEmployeeHubHolidays'],
        anyOrganisationPermissions: ['EditEmployeeLeave'],
        feature: 'HolidayAllowanceDashboard'
      },
      {
        label: 'Employee Leave Calendar',
        route: employeeHubRoutes.holidays,
        anyFeaturePackPermissions: ['AccessSickness'],
        anyOrganisationPermissions: ['EditEmployeeSickness'],
        feature: 'AdminHubCalendar'
      }
    ]);
  };

  /**
   * Watch for app navigation route changes so that the active burger bar menu item can be highlighted
   */
  private watchForNavigationChanges() {
    /** watch for route changes and set the relevant menu item as current */
    this.navigationRoutingService.navigationEvents$.subscribe((evt) => {
      const burgerBarItems = this.burgerBarItems$.value;
      if (!burgerBarItems) return;

      const routeData = this.navigationRoutingService.getRouteData();

      let currentAction = burgerBarItems.find((item) => item.feature === routeData.feature || item.section === routeData.section);

      this.appLayoutStateService.updateBurgerBar({ activeItem: currentAction });
      this.logger.trace('BurgerBarService: current link:', currentAction);
    });
  }

  /**
   * Called when the user clicks a menu item. Ensures the burger bar is closed by toggling models.
   */
  navigate(routeAction: RouteAction) {
    const route = routeAction.route;
    this.logger.trace('BURGER BAR: navigating: ', route);
    this.toggleBurgerBar(false);
    this.router.navigateByUrl(route).then();
  }

  /**
   * Toggle the burger bar; optionally force the models by parameter
   */
  toggleBurgerBar(opened?: boolean) {
    this.logger.trace('BURGER BAR: toggling > ', opened);
    const burgerBar = this.appLayoutStateService.getBurgerBar();
    const visible = opened === undefined ? !burgerBar.visible : opened;

    this.logger.trace('BURGER BAR: now visible? ', visible);

    this.appLayoutStateService.updateBurgerBar({ visible });
  }
}
